import * as React from 'react';
import Select, {Option} from 'react-select';
import { isEmpty, get } from 'lodash';
import { FieldArray } from 'formik';

import {
  CheckboxInput,
  HelpField,
  IArtifact,
  IExpectedArtifact,
  IFormikStageConfigInjectedProps,
  IPipeline,
  MapEditor,
  StageArtifactSelectorDelegate,
  StageConfigField,
  TextInput,
  FormikFormField
} from '@spinnaker/core';
import { TerraformService } from '../TerraformService';

interface ITerraformStageFormState {
  availableVersions: string[];
  availableProfiles: string[];
}

const noTerraformVersionsConfigured: Option = {
  value: 'NO_TERRAFORM_VERSIONS_CONFIGURED',
  label: 'NO_TERRAFORM_VERSIONS_CONFIGURED'
};

const actionOptions: Option[] = [
  { value: 'plan', label: 'Plan' },
  { value: 'apply', label: 'Apply' },
  { value: 'destroy', label: 'Destroy' },
  { value: 'output', label: 'Output' },
  { value: 'show', label: 'Show' },
];

const semverCompare = (a, b) => {
  const pa = a.split('.');
  const pb = b.split('.');
  for (let i = 0; i < 3; i++) {
      const na = Number(pa[i]);
      const nb = Number(pb[i]);
      if (na > nb) {return 1};
      if (nb > na) {return -1};
      if (!isNaN(na) && isNaN(nb)) {return 1};
      if (isNaN(na) && !isNaN(nb)) {return -1};
  }
  return 0;
};


export class TerraformStageForm extends React.Component<
  IFormikStageConfigInjectedProps,
  ITerraformStageFormState
  >  {

  public state = {
    availableVersions: [],
    availableProfiles: []
  };

  public componentDidMount() {
    TerraformService.getVersions()
      .then((versions: string[]) => {
        this.setState({ availableVersions: versions });
      });
    TerraformService.getProfiles()
      .then((profiles: string[]) => {
        this.setState({ availableProfiles: profiles })
      })
  }

  private onArtifactEdited = (artifact: IArtifact, index: number) => {
    this.props.formik.setFieldValue(`artifacts[${index}].id`, null);
    this.props.formik.setFieldValue(`artifacts[${index}].artifact`, artifact);
    this.props.formik.setFieldValue(`artifacts[${index}].account`, artifact.artifactAccount);
  };

  private onArtifactSelected = (id: string, index: number) => {
    this.props.formik.setFieldValue(`artifacts[${index}].id`, id);
    this.props.formik.setFieldValue(`artifacts[${index}].artifact`, null);
  };

  private onArtifactAccountSelected = (account: string, index: number) => {
    this.props.formik.setFieldValue(`artifacts[${index}].account`, account);
  };

  private addArtifact = () => {
    const stage = this.props.formik.values;
    const artifacts = [
      ...stage.artifacts,
      {
        account: '',
        id: '',
      },
    ];

    this.props.formik.setFieldValue('artifacts', artifacts);
  };

  private removeArtifact = (index: number) => {
    const stage = this.props.formik.values;
    const artifacts = [...stage.artifacts];
    artifacts.splice(index, 1);
    this.props.formik.setFieldValue('artifacts', artifacts);
  };

  private getArtifact = (stage: any, index: number) => {
    if (isEmpty(stage.artifacts) || isEmpty(stage.artifacts[index])) {
      return {
        account: '',
        id: '',
      };
    }

    return stage.artifacts[index];
  };

  private getBackendArtifact = (stage: any) => {
    if (isEmpty(stage.backendArtifact)) {
      return {
        account: '',
        id: '',
      };
    }

    return stage.backendArtifact;
  };

  private versionOptions = () => {
    const { availableVersions } = this.state;
    return availableVersions.sort(semverCompare).reverse().map(v => ({label: v, value: v}));
  };

  private profilesOptions = () => {
    const { availableProfiles } = this.state;
    const options = availableProfiles.map(v => ({ label: v, value: v }));
    options.unshift({ label: 'None', value: '' });
    return options;
  };

  public render() {
    const stage = this.props.formik.values;
    const backendArtifact = this.getBackendArtifact(stage);
    const { targets } = stage;
    const defaultVersion = this.versionOptions().length > 0 ? this.versionOptions()[0] : undefined;
    if (!stage.terraformVersion) {
      stage.terraformVersion = defaultVersion?.value;
    }
    return (
      <div className="form-horizontal clearfix">
        <div className="container-fluid form-horizontal">
          <h4>Basic Settings</h4>
          <StageConfigField fieldColumns={3} label={'Terraform Version'}>
            <Select
              className="form-control input-sm"
              clearable={false}
              required={true}
              options={this.versionOptions()}
              value={stage.terraformVersion || noTerraformVersionsConfigured}
              onChange={(o: any) => this.props.formik.setFieldValue('terraformVersion', o.value)}
            />
          </StageConfigField>
          <StageConfigField fieldColumns={3} label={'Action'}>
            <Select
              clearable={false}
              required={true}
              options={actionOptions}
              value={stage.action}
              onChange={(o: any) => this.props.formik.setFieldValue('action', o.value)}
            />
          </StageConfigField>
          <StageConfigField
            fieldColumns={3}
            helpKey={'terraform.profiles'}
            label={'Profile'}
          >
            <Select
              className="form-control input-sm"
              clearable={false}
              required={true}
              options={this.profilesOptions()}
              value={stage.profile || ''}
              onChange={(o: any) => this.props.formik.setFieldValue('profile', o.value)}
            />
          </StageConfigField>
          {stage.action === 'plan' && <StageConfigField
            fieldColumns={6}
            helpKey={'terraform.savePlanOutput'}
            label={'Save Plan Output'}
          >
            <CheckboxInput
              value={stage.savePlanOutput === undefined ? true : stage.savePlanOutput}
              onChange={(e: React.ChangeEvent<any>) => this.props.formik.setFieldValue('savePlanOutput', e.target.checked)}
            />
          </StageConfigField>}
          {stage.action === 'plan' && <StageConfigField
            fieldColumns={6}
            helpKey={'terraform.planForDestroy'}
            label={'Plan for Destroy'}
          >
            <CheckboxInput
              value={stage.planForDestroy}
              onChange={(e: React.ChangeEvent<any>) => this.props.formik.setFieldValue('planForDestroy', e.target.checked)}
            />
          </StageConfigField>}
          {(stage.action === 'apply' || stage.action === 'destroy') && <StageConfigField
            fieldColumns={6}
            helpKey={'terraform.lock'}
            label={'Ignore Locking'}
          >
            <CheckboxInput
              // This section handles the -lock=false flag for terraform commands.
              // The logic is a little backward for this because -lock=true on the terraform command line is the default
              // and what most people will use. Standard UX should have the default setting be unchecked and users take
              // the action to check the box if they want non-default behavior.
              // So that's why the wording for the checkbox is "Ignore Locking". If checked/true it needs to translate
              // to "{lock: false}" in the JSON we send to terraformer.
              // If the field is null or unchecked, we send "{lock: true}" or nothing, since -lock=true is the default.
              value={(stage.lock != null && !stage.lock)}
              onChange={(e: React.ChangeEvent<any>) => this.props.formik.setFieldValue('lock', !e.target.checked)}
            />
          </StageConfigField>}
          {
            !(stage.action === 'output' || stage.action === 'show') && (
              <>
                <StageConfigField fieldColumns={6} label="Targets" helpKey="terraform.targets">
                  <FieldArray
                    name="targets"
                    render={targetsArrayHelpers => {
                      return (
                        <table>
                          <tbody>
                            {(targets || []).map((_: string, idx: number) => (
                              <tr key={idx}>
                                <td>
                                  <div>
                                    <FormikFormField
                                      name={`targets[${idx}]`}
                                      layout={({ input }) => (<div>{input}</div>)}
                                      input={props => (<TextInput {...props} />)}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <a className="btn btn-link sm-label" onClick={() => targetsArrayHelpers.remove(idx)}>
                                    <span className="glyphicon glyphicon-trash" />
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={1}>
                                <button className="add-new btn btn-block btn-sm" onClick={() => targetsArrayHelpers.push('')}>
                                  <span className="glyphicon glyphicon-plus-sign" /> Add Target
                              </button>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      );
                    }}
                  />
                </StageConfigField>
                <StageConfigField
                  fieldColumns={6}
                  label={'Workspace'}
                  helpKey={'terraform.workspace'}
                >
                  <TextInput
                    onChange={(e: React.ChangeEvent<any>) => this.props.formik.setFieldValue('workspace', e.target.value)}
                    value={stage.workspace}
                  />
                </StageConfigField>
              </>
            )
          }

          <h4>Main Terraform Artifact <HelpField id={'terraform.mainArtifact'} /></h4>
          <StageArtifactSelectorDelegate
            artifact={this.getArtifact(stage, 0).artifact}
            fieldColumns={6}
            excludedArtifactTypePatterns={[]}
            expectedArtifactId={this.getArtifact(stage, 0).id}
            label={'Expected Artifact'}
            onArtifactEdited={artifact => {this.onArtifactEdited(artifact, 0)}}
            onExpectedArtifactSelected={artifact => this.onArtifactSelected(artifact.id, 0)}
            pipeline={this.props.pipeline}
            stage={stage}
          />
          <StageConfigField
            fieldColumns={6}
            label={'Subdirectory'}
            helpKey={'terraform.subdirectory'}
          >
            <TextInput
              onChange={(e: React.ChangeEvent<any>) => this.props.formik.setFieldValue('dir', e.target.value)}
              value={stage.dir}
            />
          </StageConfigField>
          {stage.action !== 'show' && (
            <>
              <h4>Terraform Artifacts <HelpField id={'terraform.variableFiles'} /></h4>
              {!isEmpty(stage.artifacts) && (
                <>
                  <div className="row form-group">
                    {stage.artifacts.slice(1).map((a: any, index: number) => {
                      return (
                        <div key={index}>
                          <div className="col-md-offset-1 col-md-8">
                            <StageArtifactSelectorDelegate
                              fieldColumns={9}
                              artifact={a.artifact}
                              excludedArtifactTypePatterns={[]}
                              expectedArtifactId={a.id}
                              label={'Expected Artifact'}
                              onArtifactEdited={artifact => this.onArtifactEdited(artifact, index + 1)}
                              onExpectedArtifactSelected={artifact =>
                                this.onArtifactSelected(artifact.id, index + 1)
                              }
                              pipeline={this.props.pipeline}
                              stage={stage}
                            />
                          </div>
                          <div className="col-md-1">
                            <div className="form-control-static">
                              <a className="clickable" onClick={() => this.removeArtifact(index + 1)}>
                                <span className="glyphicon glyphicon-trash" />
                                <span className="sr-only">Remove field</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <StageConfigField fieldColumns={6} label={''} >
                <button className="btn btn-block btn-sm add-new" onClick={() => this.addArtifact()}>
                  <span className="glyphicon glyphicon-plus-sign" />
                  Add variable file
                </button>
              </StageConfigField>
            </>
          )}
          {stage.action === 'show' && (
            <>
              <h4>Terraform Plan <HelpField id={'terraform.plan'} /></h4>
              <div className="row form-group">
                <div className="col-md-offset-1 col-md-8">
                  <StageArtifactSelectorDelegate
                    artifact={this.getArtifact(stage, 1).artifact}
                    fieldColumns={9}
                    excludedArtifactTypePatterns={[]}
                    expectedArtifactId={this.getArtifact(stage, 1).id}
                    label={'Expected Artifact'}
                    onArtifactEdited={artifact => {this.onArtifactEdited(artifact, 1)}}
                    onExpectedArtifactSelected={artifact => this.onArtifactSelected(artifact.id, 1)}
                    pipeline={this.props.pipeline}
                    stage={stage}
                  />
                </div>
              </div>
            </>
          )}
          {!(stage.action === 'output' || stage.action === 'show') && (
            <>
              <h4>Variable Overrides <HelpField id={'terraform.overrides'} /></h4>
              <StageConfigField fieldColumns={6} label={'Overrides'}>
                <MapEditor
                  addButtonLabel={'Add override'}
                  model={stage.overrides || {}}
                  allowEmpty={true}
                  onChange={(o: any) => this.props.formik.setFieldValue('overrides', o)}
                />
              </StageConfigField>
            </>
          )}
          <h4>Backend Artifact <HelpField id={'terraform.backendArtifact'} /></h4>
          <StageArtifactSelectorDelegate
            artifact={backendArtifact.artifact}
            fieldColumns={6}
            excludedArtifactTypePatterns={[]}
            expectedArtifactId={backendArtifact.id}
            label={'Expected Artifact'}
            pipeline={this.props.pipeline}
            stage={stage}
            onArtifactEdited={(artifact: IArtifact) => {
              this.props.formik.setFieldValue('backendArtifact.id', null);
              this.props.formik.setFieldValue('backendArtifact.artifact', artifact);
              this.props.formik.setFieldValue('backendArtifact.account', artifact.artifactAccount);
            }}
            onExpectedArtifactSelected={(artifact: IExpectedArtifact) => {
              this.props.formik.setFieldValue('backendArtifact.id', artifact.id);
              this.props.formik.setFieldValue('backendArtifact.artifact', null);
            }}
          />
        </div>
      </div>
    )
  }
}
